import { useEffect, useState } from 'react';

/**
 * Polling one camera's live-check frame off the appliance's localhost preview.
 *
 * Shared by the single-camera dialog and the all-cameras grid so both report
 * the same states. It fetches rather than pointing an `<img>` at the URL,
 * because the status code is the whole diagnosis: an `<img>` can only tell you
 * "loaded" or "didn't", so a camera that is busy recording looks identical to
 * one that is still warming up, and the operator watches a spinner forever.
 *
 * The preview is localhost-only by design — only a browser ON the appliance can
 * reach it, which is what stops cameras being viewable from anywhere else.
 */

export const LIVE_PREVIEW_BASE = 'http://localhost:8899';

/** ~6 fps, matching the agent's burst rate — for a single camera being aimed. */
export const POLL_MS_SINGLE = 166;
/**
 * ~2 fps for the all-cameras grid. The grid answers "is each camera alive and
 * pointed the right way", which does not need motion fidelity — and polling
 * every camera at 6 fps multiplies the appliance's JPEG encode load by the
 * number of cameras, on a box whose day job is recording. A third of the rate
 * across N tiles is the difference between a health check and a burden.
 */
export const POLL_MS_GRID = 500;

export type LiveFrameStatus =
  /** Nothing fetched yet. */
  | 'connecting'
  /** Reached the camera, no frame decoded yet (agent returns 204). */
  | 'warming'
  /** A frame is showing. */
  | 'live'
  /** The camera is recording — the appliance refused (409). A camera serves
   *  one consumer, so previewing it would risk the clip. */
  | 'recording'
  /** The appliance doesn't know this camera (404). */
  | 'unknown-camera'
  /** The preview server didn't answer at all. */
  | 'unreachable';

export interface LiveFrame {
  status: LiveFrameStatus;
  /** Object URL of the newest frame, or null. */
  src: string | null;
}

/**
 * Poll one camera while `enabled`. Stopping the poll is what releases the
 * camera: the agent's burst idle-stops a few seconds after the last request,
 * so a closed dialog frees the device without an explicit "stop" call.
 */
export function useLiveFrame(
  cameraId: string,
  enabled = true,
  pollMs: number = POLL_MS_SINGLE,
): LiveFrame {
  const [status, setStatus] = useState<LiveFrameStatus>('connecting');
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    let frameTimer: number | undefined;
    let statusTimer: number | undefined;

    const urlFor = () =>
      `${LIVE_PREVIEW_BASE}/live/${encodeURIComponent(cameraId)}.jpg?t=${Date.now()}`;

    // DISPLAY: preload into an Image and swap only once decoded, pointing the
    // <img> straight at the URL.
    //
    // Deliberately NOT fetch()+createObjectURL. That was tried and produced
    // broken images with a "live" badge: the agent had the frames (hundreds of
    // them) but an object URL revoked while the <img> was still resolving it
    // leaves a dead src on screen, and the lifetime is near-impossible to get
    // right at polling rates. A plain URL has no lifetime to manage, and the
    // browser's own cache-busting query does the rest. A 204 (warming up)
    // simply fails the load, so we keep the last good frame.
    const pollFrame = () => {
      if (stopped) return;
      const url = urlFor();
      const pre = new Image();
      pre.onload = () => {
        if (!stopped) {
          setSrc(url);
          setStatus('live');
        }
      };
      pre.onerror = () => {
        /* 204 warm-up / transient — the status probe below owns the verdict */
      };
      pre.src = url;
    };

    // STATUS: a separate, slower probe reads the HTTP code, which is the only
    // way to tell "busy recording" (409) from "warming up" (204) — an <img>
    // cannot report either. HEAD so it costs no frame bytes.
    const pollStatus = async () => {
      if (stopped) return;
      try {
        const r = await fetch(urlFor(), { method: 'HEAD', cache: 'no-store' });
        if (stopped) return;
        if (r.status === 409) {
          setSrc(null);
          setStatus('recording');
        } else if (r.status === 404) {
          setSrc(null);
          setStatus('unknown-camera');
        } else if (r.status === 204) {
          setStatus((prev) => (prev === 'live' ? prev : 'warming'));
        }
      } catch {
        if (!stopped) setStatus('unreachable');
      }
    };

    pollFrame();
    void pollStatus();
    frameTimer = window.setInterval(pollFrame, pollMs);
    statusTimer = window.setInterval(() => void pollStatus(), 1000);
    return () => {
      stopped = true;
      if (frameTimer) window.clearInterval(frameTimer);
      if (statusTimer) window.clearInterval(statusTimer);
    };
  }, [cameraId, enabled, pollMs]);

  return { status, src };
}

/**
 * Whether this browser can reach the appliance's preview server at all — i.e.
 * whether the page is open ON the appliance. Checked once per dialog so the
 * grid can show one clear explanation instead of every tile failing separately.
 */
export function useLivePreviewReachable(): boolean | null {
  const [reachable, setReachable] = useState<boolean | null>(null);
  useEffect(() => {
    const ctrl = new AbortController();
    const to = window.setTimeout(() => ctrl.abort(), 1500);
    void fetch(`${LIVE_PREVIEW_BASE}/ping`, { signal: ctrl.signal })
      .then(() => setReachable(true))
      .catch(() => setReachable(false))
      .finally(() => window.clearTimeout(to));
    return () => {
      ctrl.abort();
      window.clearTimeout(to);
    };
  }, []);
  return reachable;
}

/** One-line operator copy per state. Kept here so both views say the same thing. */
export function liveFrameMessage(status: LiveFrameStatus): string {
  switch (status) {
    case 'recording':
      return 'Recording — cannot test while in use';
    case 'unknown-camera':
      return 'Not registered on this appliance';
    case 'unreachable':
      return 'No answer from the appliance';
    case 'warming':
      return 'Starting…';
    case 'connecting':
      return 'Connecting…';
    default:
      return '';
  }
}
